/** A repeatable stack of empty returnable timber pallets. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

function part(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const result = new THREE.Mesh(geometry, material);
  result.name = name;
  result.castShadow = true;
  result.receiveShadow = true;
  return result;
}

/**
 * Builds the stock-yard stack without random variation: preview references need
 * the same battered arrangement on every run, rather than a screenshot changing
 * because the factory happened to be called again.
 */
export function buildPalletStack(levels: number): THREE.Group {
  // The loading-yard room places 4-, 7-, and 11-pallet stacks; an out-of-range
  // request is a placement typo that must be loud, not silently clamped into a
  // different piece of cover with a different height and silhouette.
  if (!Number.isInteger(levels) || levels < 2 || levels > 12) {
    throw new RangeError(`Pallet stack levels must be an integer from 2 to 12; received ${levels}.`);
  }

  const group = new THREE.Group();
  group.name = 'pallet-stack';
  const topBoardTimber = surface('organic', 0x8a7f6c);
  const bearerTimber = surface('organic', 0x6b6252);
  const boardGeometry = new THREE.BoxGeometry(1.2, 0.022, 0.16);
  const bearerGeometry = new THREE.BoxGeometry(1.2, 0.075, 0.10);
  const slabGeometry = new THREE.BoxGeometry(1.2, 0.022, 1.0);
  for (let index = 0; index < levels; index += 1) {
    const sign = index % 2 === 0 ? 1 : -1;
    const pallet = new THREE.Group();
    pallet.name = `timber-pallet-${index + 1}`;
    pallet.position.set(sign * 0.02, index * 0.145, sign * 0.02);
    pallet.rotation.y = sign * 0.03;
    for (const [z, name] of [[-0.42, 'top-board-back'], [0, 'top-board-centre'], [0.42, 'top-board-front']] as const) {
      const board = part(boardGeometry, topBoardTimber, `${name}-${index + 1}`);
      board.position.set(0, 0.134, z);
      pallet.add(board);
    }
    for (const [z, name] of [[-0.45, 'bearer-back'], [0, 'bearer-centre'], [0.45, 'bearer-front']] as const) {
      const bearer = part(bearerGeometry, bearerTimber, `${name}-${index + 1}`);
      bearer.position.set(0, 0.0645, z);
      pallet.add(bearer);
    }
    const slab = part(slabGeometry, topBoardTimber, `bottom-slab-${index + 1}`);
    slab.position.set(0, 0.011, 0);
    pallet.add(slab);
    group.add(pallet);
  }

  return group;
}

export const palletStackAsset: AssetDefinition = {
  id: 'pallet-stack',
  label: 'Pallet stack',
  description: 'A short stack of weathered empty timber pallets.',
  footprint: { width: 1.28, height: 1.02, length: 1.08 },
  create: () => buildPalletStack(7),
};
